<template>
  <div class="eg-wrap">

    <!-- Day picker -->
    <div class="cm-panel eg-head">
      <div class="cm-panel-head">
        <div>
          <div class="cm-panel-title">Episode Grouper</div>
          <div class="eg-sub">
            One day of ADT by patient — algorithmic grouping on the left, LLM parser on the right
          </div>
        </div>
        <div class="cm-panel-head-actions">
          <input
            v-model="patient" placeholder="Filter by MBI…"
            class="cm-input cm-input-sm" style="width: 150px;"
            @keyup.enter="load"
          />
          <label class="eg-toggle">
            <input type="checkbox" v-model="onlyDisagree" /> Disagreements only
          </label>
          <div class="cm-stepper">
            <button class="cm-icon-btn" @click="shiftDay(-1)">
              <FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 10px;" />
            </button>
            <input class="cm-input cm-input-sm" type="date" v-model="day" @change="load"
                   style="width: 132px; text-align: center;" />
            <button class="cm-icon-btn" @click="shiftDay(1)" :disabled="isToday">
              <FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 10px;" />
            </button>
          </div>
          <button class="cm-btn cm-btn-sm" :class="isToday ? 'cm-btn-primary' : 'cm-btn-secondary'"
                  @click="goToday">Today</button>
          <button class="cm-icon-btn" @click="load" title="Refresh">
            <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" />
          </button>
        </div>
      </div>
      <div v-if="!loading && !error" class="eg-meta">
        <strong>{{ patients.length }}</strong> patients ·
        <strong>{{ algorithmic.length }}</strong> algorithmic episodes ·
        <strong>{{ llm.length }}</strong> LLM encounters
        <span v-if="disagreeCount" class="eg-meta-warn">
          · {{ disagreeCount }} patient{{ disagreeCount === 1 ? '' : 's' }} where the two disagree
        </span>
      </div>
    </div>

    <div v-if="loading" class="cm-loading"><div class="cm-spinner"></div></div>
    <div v-else-if="error" class="cm-panel eg-error">{{ error }}</div>

    <!-- Patient list -->
    <div v-else class="cm-panel eg-body">
      <div v-if="!patients.length" class="eg-empty">No ADT activity on this day.</div>

      <div v-for="p in patients" :key="p.mbi" class="eg-patient" :class="{ open: isOpen(p.mbi) }">

        <!-- Patient row -->
        <div class="eg-patient-head" @click="toggle(p.mbi)">
          <FontAwesomeIcon
            :icon="['fas', isOpen(p.mbi) ? 'chevron-down' : 'chevron-right']"
            class="eg-chev"
          />
          <code class="eg-mbi">{{ p.mbi }}</code>
          <span class="eg-counts">
            <span class="eg-pill left">{{ p.algorithmic.length }}</span>
            <span class="eg-vs" :class="{ differ: p.differs }">vs</span>
            <span class="eg-pill right">{{ p.llm.length }}</span>
          </span>
          <span v-if="p.differs" class="eg-flag">{{ p.differsLabel }}</span>
          <span class="eg-spacer"></span>
          <span class="eg-fac" :title="p.facility">{{ p.facility || '—' }}</span>
          <span class="eg-times">{{ shortDT(p.latest) }}</span>
        </div>

        <!-- Two columns for this patient -->
        <div v-if="isOpen(p.mbi)" class="eg-cols">
          <div v-for="side in sides" :key="side.key" class="eg-col">
            <div class="eg-col-head">
              {{ side.title }}
              <span class="eg-col-sub">{{ side.subtitle }}</span>
            </div>

            <div v-if="!p[side.key].length" class="eg-col-empty">None</div>

            <div
              v-for="ep in p[side.key]" :key="ep.group_id"
              class="eg-item"
              :class="{ open: isOpen(ep.group_id), linked: isLinked(ep.group_id) }"
              @mouseenter="hover = ep"
              @mouseleave="hover = null"
            >
              <div class="eg-item-head" @click="toggle(ep.group_id)">
                <FontAwesomeIcon
                  :icon="['fas', isOpen(ep.group_id) ? 'chevron-down' : 'chevron-right']"
                  class="eg-chev sm"
                />
                <span class="eg-type" :class="typeClass(ep.type)">{{ shortType(ep.type) }}</span>
                <span class="eg-times">{{ shortDT(ep.start) }} → {{ ep.end ? shortDT(ep.end) : 'open' }}</span>
                <span class="eg-spacer"></span>
                <span v-if="ep.counterpart_count > 1" class="eg-flag sm"
                      :title="side.key === 'algorithmic'
                        ? 'The LLM split this into ' + ep.counterpart_count + ' encounters'
                        : 'This spans ' + ep.counterpart_count + ' algorithmic episodes'">
                  {{ ep.counterpart_count }}×
                </span>
                <span v-if="ep.visit_numbers > 1" class="eg-flag sm"
                      :title="ep.visit_numbers + ' distinct visit numbers'">
                  {{ ep.visit_numbers }}v
                </span>
                <span class="eg-evcount">{{ ep.event_count }}</span>
              </div>

              <div class="eg-item-sub">
                <span class="eg-fac" :title="ep.facility">{{ ep.facility || '—' }}</span>
                <span v-if="ep.disposition" class="eg-dispo">→ {{ ep.disposition }}</span>
                <span v-if="ep.status" class="eg-status" :class="{ incomplete: ep.status === 'incomplete' }">
                  {{ ep.status }}
                </span>
              </div>

              <!-- Events -->
              <div v-if="isOpen(ep.group_id)" class="eg-events">
                <div v-if="ep.rationale" class="eg-rationale">{{ ep.rationale }}</div>
                <table class="eg-evtable">
                  <tbody>
                    <tr v-for="(e, i) in ep.events" :key="i" :class="{ offday: !e.on_day }">
                      <td class="eg-ev-time">{{ shortDT(e.event_time) }}</td>
                      <td class="eg-ev-type">{{ e.event_type || '—' }}</td>
                      <td class="eg-ev-set">{{ e.care_setting || '—' }}</td>
                      <td class="eg-ev-fac" :title="e.facility">{{ e.facility || '—' }}</td>
                      <td class="eg-ev-visit">{{ e.visit_number || '—' }}</td>
                      <td class="eg-ev-cp" :title="counterpartTitle(side.key, e)">
                        {{ e.counterpart_id ? shortId(e.counterpart_id) : '—' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const SIDES = [
  { key: 'algorithmic', title: 'Algorithmic', subtitle: '72h gap rule' },
  { key: 'llm', title: 'LLM parser', subtitle: 'nightly resolver' },
];

export default {
  name: 'CareManagementEpisodeGrouper',
  components: { FontAwesomeIcon },
  data() {
    return {
      day: new Date().toISOString().slice(0, 10),
      patient: '',
      onlyDisagree: false,
      algorithmic: [],
      llm: [],
      sides: SIDES,
      open: {},
      hover: null,
      loading: false,
      error: '',
    };
  },
  computed: {
    isToday() { return this.day === new Date().toISOString().slice(0, 10); },

    // One row per patient, both sides nested underneath. Grouped client-side
    // because the payload already carries patient_identifier on every grouping —
    // no second round trip, and the two sides stay from the same read.
    patients() {
      const by = new Map();
      const put = (side, ep) => {
        const mbi = ep.patient_identifier;
        if (!by.has(mbi)) by.set(mbi, { mbi, algorithmic: [], llm: [] });
        by.get(mbi)[side].push(ep);
      };
      this.algorithmic.forEach(e => put('algorithmic', e));
      this.llm.forEach(e => put('llm', e));

      const rows = [...by.values()].map(p => {
        const all = [...p.algorithmic, ...p.llm];
        // A patient "differs" when the two engines produced a different NUMBER of
        // groupings, or any single grouping maps to more than one on the other
        // side. Count alone is not enough: 2-vs-2 can still be a boundary
        // disagreement if the events are split differently.
        const countDiff = p.algorithmic.length !== p.llm.length;
        const spans = all.some(e => (e.counterpart_count || 0) > 1);
        return {
          ...p,
          differs: countDiff || spans,
          differsLabel: countDiff
            ? `${p.algorithmic.length} vs ${p.llm.length}`
            : 'split differently',
          latest: all.reduce((mx, e) => (e.end && e.end > mx ? e.end : mx), ''),
          facility: (p.llm[0] || p.algorithmic[0] || {}).facility,
        };
      });

      const filtered = this.onlyDisagree ? rows.filter(r => r.differs) : rows;
      // Disagreements first, then most recent — the reason to open this page is
      // to look at the ones that disagree.
      return filtered.sort((a, b) =>
        (b.differs - a.differs) || (b.latest || '').localeCompare(a.latest || ''));
    },

    disagreeCount() {
      const seen = new Map();
      this.algorithmic.forEach(e => {
        const v = seen.get(e.patient_identifier) || { a: 0, l: 0, s: false };
        v.a += 1; v.s = v.s || (e.counterpart_count || 0) > 1;
        seen.set(e.patient_identifier, v);
      });
      this.llm.forEach(e => {
        const v = seen.get(e.patient_identifier) || { a: 0, l: 0, s: false };
        v.l += 1; v.s = v.s || (e.counterpart_count || 0) > 1;
        seen.set(e.patient_identifier, v);
      });
      return [...seen.values()].filter(v => v.a !== v.l || v.s).length;
    },

    // Group ids on the other side that the hovered grouping maps to.
    hoveredCounterparts() {
      return new Set(this.hover?.counterpart_ids || []);
    },
  },
  methods: {
    isOpen(id) { return !!this.open[id]; },
    toggle(id) { this.open = { ...this.open, [id]: !this.open[id] }; },
    isLinked(id) { return this.hoveredCounterparts.has(id); },
    shortId(v) {
      if (!v) return '—';
      return v.includes('|') ? `ep ${v.split('|')[1]}` : v.slice(0, 8);
    },
    counterpartTitle(sideKey, e) {
      if (!e.counterpart_id) return 'Not claimed by the other parser';
      return sideKey === 'algorithmic'
        ? `LLM encounter ${e.counterpart_id}`
        : `Algorithmic ${e.counterpart_id}`;
    },
    shortType(t) {
      return ({
        INPATIENT: 'IP', ED_VISIT: 'ED', OBSERVATION_STAY: 'OBS',
        OUTPATIENT_PROCEDURE: 'OP', OTHER: 'OTH',
      })[t] || t || '—';
    },
    typeClass(t) {
      return { INPATIENT: 'tp-ip', ED_VISIT: 'tp-ed', OBSERVATION_STAY: 'tp-obs' }[t] || 'tp-other';
    },
    shortDT(v) {
      if (!v) return '—';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString(undefined, {
        month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    },
    shiftDay(n) {
      const d = new Date(this.day + 'T12:00:00');
      d.setDate(d.getDate() + n);
      this.day = d.toISOString().slice(0, 10);
      this.load();
    },
    goToday() {
      this.day = new Date().toISOString().slice(0, 10);
      this.load();
    },
    async load() {
      this.loading = true;
      this.error = '';
      this.open = {};
      try {
        const { data } = await this.$axios.post('/api/care-management/episode-grouper/', {
          day: this.day,
          patient_identifier: this.patient || undefined,
        });
        this.algorithmic = data.algorithmic || [];
        this.llm = data.llm || [];
      } catch (e) {
        this.error = e.response?.data?.error || 'Could not load the day.';
        this.algorithmic = [];
        this.llm = [];
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() { this.load(); },
};
</script>

<style scoped>
.eg-wrap { padding: 16px; background: #F8F8F9; display: flex; flex-direction: column; gap: 12px; height: 100%; box-sizing: border-box; }
.eg-head { flex-shrink: 0; }
.eg-sub { font-size: 11.5px; color: #8B8B8D; margin-top: 2px; }
.eg-meta { padding: 8px 16px; font-size: 11.5px; color: #8B8B8D; border-top: 1px solid #F1F1F2; }
.eg-meta strong { color: #626264; }
.eg-meta-warn { color: #B45309; }
.eg-error { padding: 16px; color: #B91C1C; font-size: 12px; }
.eg-empty { padding: 24px 16px; font-size: 12px; color: #8B8B8D; text-align: center; }
.eg-toggle { font-size: 11.5px; color: #626264; display: flex; align-items: center; gap: 5px; cursor: pointer; white-space: nowrap; }

.eg-body { flex: 1; min-height: 0; overflow-y: auto; }

/* ---- patient row ---- */
.eg-patient { border-bottom: 1px solid #E8E9E9; }
.eg-patient.open { background: #FCFCFD; }
.eg-patient-head { display: flex; align-items: center; gap: 9px; padding: 9px 14px; cursor: pointer; font-size: 12px; }
.eg-patient-head:hover { background: #F5F5F6; }
.eg-chev { font-size: 10px; color: #A0A0A2; width: 10px; flex-shrink: 0; }
.eg-chev.sm { font-size: 8px; width: 8px; }
.eg-spacer { flex: 1; }
.eg-mbi { font-size: 11.5px; background: #F1F1F2; padding: 2px 6px; border-radius: 3px; flex-shrink: 0; }

.eg-counts { display: flex; align-items: center; gap: 5px; flex-shrink: 0; }
.eg-pill { font-size: 11px; font-weight: 600; min-width: 20px; text-align: center; padding: 1px 6px; border-radius: 3px; background: #F1F1F2; color: #626264; }
.eg-vs { font-size: 10px; color: #B4B4B6; }
.eg-vs.differ { color: #B45309; font-weight: 600; }

/* ---- two columns per patient ---- */
.eg-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: #E8E9E9; border-top: 1px solid #E8E9E9; }
.eg-col { background: #FFF; min-width: 0; }
.eg-col-head { font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #8B8B8D; padding: 6px 12px; background: #FAFAFB; border-bottom: 1px solid #F1F1F2; }
.eg-col-sub { font-weight: 400; text-transform: none; letter-spacing: 0; margin-left: 6px; color: #B4B4B6; }
.eg-col-empty { padding: 12px; font-size: 11px; color: #B4B4B6; text-align: center; }

/* ---- episode within a column ---- */
.eg-item { border-bottom: 1px solid #F5F5F6; }
.eg-item:last-child { border-bottom: none; }
.eg-item.open { background: #FCFCFD; }
.eg-item.linked { box-shadow: inset 3px 0 0 #F76734; background: #FFF7ED; }
.eg-item-head { display: flex; align-items: center; gap: 6px; padding: 6px 12px 1px; cursor: pointer; font-size: 11.5px; }
.eg-item-head:hover { background: #FAFAFB; }

.eg-type { font-size: 9.5px; font-weight: 700; padding: 1px 5px; border-radius: 3px; flex-shrink: 0; }
.tp-ip { background: #FEE2E2; color: #991B1B; }
.tp-ed { background: #FEF3C7; color: #92400E; }
.tp-obs { background: #DBEAFE; color: #1E40AF; }
.tp-other { background: #F1F1F2; color: #626264; }

.eg-times { font-size: 11px; color: #626264; white-space: nowrap; }
.eg-flag { font-size: 10px; font-weight: 600; color: #B45309; background: #FEF3C7; padding: 1px 5px; border-radius: 3px; white-space: nowrap; }
.eg-flag.sm { font-size: 9.5px; padding: 0 4px; }
.eg-evcount { font-size: 10px; color: #A0A0A2; font-variant-numeric: tabular-nums; min-width: 16px; text-align: right; }

.eg-item-sub { display: flex; gap: 7px; align-items: center; padding: 0 12px 6px 26px; font-size: 10.5px; color: #8B8B8D; }
.eg-fac { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 240px; }
.eg-dispo { color: #626264; white-space: nowrap; }
.eg-status { font-size: 9.5px; padding: 0 4px; border-radius: 3px; background: #ECFDF5; color: #047857; }
.eg-status.incomplete { background: #F1F1F2; color: #8B8B8D; }

/* ---- events ---- */
.eg-events { padding: 6px 12px 9px 26px; background: #FAFAFB; border-top: 1px solid #F1F1F2; overflow-x: auto; }
.eg-rationale { font-size: 10.5px; color: #626264; line-height: 1.5; margin-bottom: 7px; font-style: italic; }
.eg-evtable { width: 100%; border-collapse: collapse; }
.eg-evtable td { font-size: 10px; padding: 2px 6px 2px 0; color: #626264; vertical-align: top; }
/* Events outside the selected day are dimmed but SHOWN — a grouping cannot be
   judged from a one-day slice of itself. */
.eg-evtable tr.offday td { color: #B4B4B6; }
.eg-ev-time { white-space: nowrap; width: 78px; font-variant-numeric: tabular-nums; }
.eg-ev-type { font-weight: 500; white-space: nowrap; }
.eg-ev-set { white-space: nowrap; }
.eg-ev-fac { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.eg-ev-visit { white-space: nowrap; font-variant-numeric: tabular-nums; }
.eg-ev-cp { white-space: nowrap; color: #8B8B8D; font-family: ui-monospace, monospace; }
</style>
